import type { ApplyViewResult } from "@app/commands/catalog/views/applyView";
import { useRouter } from "@core/Api/useRouter";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreator from "@core-ui/ContextServices/ApiUrlCreator";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { useCallback } from "react";

/** `background`: the view follows the page's address rather than the user's choice, so a refusal stays silent. */
export const useApplyView = ({ background = false }: { background?: boolean } = {}) => {
	const apiUrlCreator = ApiUrlCreator.value;
	const router = useRouter();
	const resolvedViewId = useCatalogPropsStore((state) => state.data?.resolvedView?.id);
	const articleLogicPath = useArticlePropsStore((state) => state?.data?.logicPath);

	// biome-ignore lint/correctness/useExhaustiveDependencies: the URL creator changes with the page, and so must the callback
	const applyView = useCallback(
		async (viewId: string) => {
			const url = apiUrlCreator.applyCatalogView(viewId, articleLogicPath);
			const res = background
				? await FetchService.fetchInBackground<ApplyViewResult>(url)
				: await FetchService.fetch<ApplyViewResult>(url);
			if (!res.ok) return;

			const isReset = resolvedViewId === viewId;
			const { redirectTo } = await res.json();
			if (redirectTo) {
				await router.pushPath(redirectTo, isReset ? undefined : { view: viewId });
				return;
			}

			const searchParams = new URLSearchParams(window.location.search);
			if (isReset) searchParams.delete("view");
			else searchParams.set("view", viewId);

			const search = searchParams.toString();
			history.replaceState(
				null,
				"",
				window.location.pathname + (search ? `?${search}` : "") + window.location.hash,
			);
			refreshPage();
		},
		[apiUrlCreator, resolvedViewId, articleLogicPath, router, background],
	);

	return applyView;
};
